import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import { Switch } from "./ui/switch";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Building2,
  Plus,
  ArrowLeft,
  MapPin,
  MoreVertical,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const FacilityManagement = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [facilities, setFacilities] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddingFacility, setIsAddingFacility] = useState(false);
  const [isEditingFacility, setIsEditingFacility] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [error, setError] = useState(null);
  const [facilityForm, setFacilityForm] = useState({
    name: "",
    location: "",
    is_active: true,
  });

  useEffect(() => {
    fetchCompanyAndFacilities();
  }, [companyId]);

  const fetchCompanyAndFacilities = async () => {
    try {
      const [companyData, facilitiesData] = await Promise.all([
        supabase.from("companies").select("*").eq("id", companyId).single(),
        supabase
          .from("facilities")
          .select("*")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false }),
      ]);

      if (companyData.error) throw companyData.error;
      if (facilitiesData.error) throw facilitiesData.error;

      setCompany(companyData.data);
      setFacilities(facilitiesData.data);
    } catch (error) {
      console.error("Error:", error);
      setError("Nepodarilo sa načítať údaje");
    } finally {
      setLoading(false);
    }
  };

  const handleAddFacility = async () => {
    try {
      const { data, error } = await supabase
        .from("facilities")
        .insert([
          {
            company_id: companyId,
            name: facilityForm.name,
            location: facilityForm.location,
            is_active: true,
          },
        ])
        .select();

      if (error) throw error;

      setFacilities([data[0], ...facilities]);
      setIsAddingFacility(false);
      resetForm();
    } catch (error) {
      console.error("Error:", error);
      setError("Nepodarilo sa pridať skupinu");
    }
  };

  const handleEditFacility = async () => {
    try {
      const { data, error } = await supabase
        .from("facilities")
        .update({
          name: facilityForm.name,
          location: facilityForm.location,
          is_active: facilityForm.is_active,
        })
        .eq("id", selectedFacility.id)
        .select();

      if (error) throw error;

      setFacilities(
        facilities.map((f) => (f.id === selectedFacility.id ? data[0] : f))
      );
      setIsEditingFacility(false);
      resetForm();
    } catch (error) {
      console.error("Error:", error);
      setError("Nepodarilo sa upraviť skupinu");
    }
  };

  const handleEditClick = (facility) => {
    setSelectedFacility(facility);
    setFacilityForm({
      name: facility.name,
      location: facility.location,
      is_active: facility.is_active,
    });
    setIsEditingFacility(true);
  };

  const resetForm = () => {
    setFacilityForm({ name: "", location: "", is_active: true });
    setSelectedFacility(null);
  };

  if (loading) {
    return <div>Načítavam...</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold">
                Skupiny - {company?.name}
              </CardTitle>
              <CardDescription>
                Správa prevádzok pre vašu spoločnosť
              </CardDescription>
            </div>
            <div className="flex space-x-4">
              <Button variant="outline" onClick={() => navigate("/companies")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Späť
              </Button>
              <Button onClick={() => setIsAddingFacility(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Pridať prevádzku
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {facilities.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">
                Žiadne prevádzky
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Začnite pridaním novej prevádzky pre vašu spoločnosť.
              </p>
              <div className="mt-6">
                <Button onClick={() => setIsAddingFacility(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Pridať prevádzku
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Názov prevádzky</TableHead>
                  <TableHead>Lokalita</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead className="w-[100px]">Akcie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {facilities.map((facility) => (
                  <TableRow key={facility.id}>
                    <TableCell className="font-medium">
                      {facility.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                        {facility.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={facility.is_active ? "success" : "secondary"}
                        className={
                          facility.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }
                      >
                        {facility.is_active ? "Aktívna" : "Neaktívna"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleEditClick(facility)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Upraviť
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isAddingFacility || isEditingFacility}
        onOpenChange={(open) => {
          if (!open) {
            resetForm();
            setIsAddingFacility(false);
            setIsEditingFacility(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditingFacility
                ? "Upraviť prevádzku"
                : "Pridať novú prevádzku"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Názov prevádzky</Label>
              <Input
                id="name"
                value={facilityForm.name}
                onChange={(e) =>
                  setFacilityForm({ ...facilityForm, name: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="location">Lokalita</Label>
              <Input
                id="location"
                value={facilityForm.location}
                onChange={(e) =>
                  setFacilityForm({ ...facilityForm, location: e.target.value })
                }
              />
            </div>

            {/* Add the new status toggle section - only show for editing */}
            {isEditingFacility && (
              <div className="flex flex-col gap-1.5">
                <Label>Stav prevádzky</Label>
                <div className="flex items-center space-x-2 rounded-md border p-4">
                  <Switch
                    checked={facilityForm.is_active}
                    onCheckedChange={(checked) =>
                      setFacilityForm({ ...facilityForm, is_active: checked })
                    }
                  />
                  <div className="space-y-0.5">
                    <Label className="text-base">
                      {facilityForm.is_active ? "Aktívna" : "Neaktívna"}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {facilityForm.is_active
                        ? "Prevádzka je aktívna a môže využívať služby"
                        : "Prevádzka je deaktivovaná a nemôže využívať služby"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddingFacility(false);
                setIsEditingFacility(false);
              }}
            >
              Zrušiť
            </Button>
            <Button
              onClick={
                isEditingFacility ? handleEditFacility : handleAddFacility
              }
            >
              {isEditingFacility ? "Uložiť" : "Pridať"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default FacilityManagement;
